import {
  HtsReadinessBand,
  isHtsReviewPending,
  type AssessmentScoreLabel,
} from '@evaltick/shared';

import type { DataTableMobileRow, DataTableRowTone } from '@/components/DataTable';
import { bandLabelFor } from '@/pages/Sessions/AdminHtsSection';
import { AssessmentStatus } from '@/services/api';
import type { AdminAssessment, AdminHtsSubmissionListItem } from '@/services/adminService';
import { formatUserName } from '@/utils/formatters';

/** A guest has only a synthetic name/email, so the access code's tags are the
 * meaningful identifier — same rule the desktop User column applies. */
const displayUser = (record: {
  accessCodeTags: string[] | null;
  user: { firstName: string | null; lastName: string | null; email: string };
}): string => {
  if (record.accessCodeTags !== null) {
    return record.accessCodeTags.length > 0 ? record.accessCodeTags.join(', ') : 'Guest';
  }
  return formatUserName(record.user.firstName, record.user.lastName) || record.user.email;
};

const HTS_BAND_TONES: Record<HtsReadinessBand, DataTableRowTone> = {
  [HtsReadinessBand.FOUNDATIONAL]: 'danger',
  [HtsReadinessBand.DEVELOPING]: 'warning',
  [HtsReadinessBand.COMPETITIVE]: 'success',
  [HtsReadinessBand.HIGHLY_COMPETITIVE]: 'success',
};

const SCORE_LABEL_TONES: Record<AssessmentScoreLabel, DataTableRowTone> = {
  Strength: 'success',
  Developing: 'warning',
  'Needs Focus': 'danger',
};

export const assessmentMobileRow = (
  record: AdminAssessment,
  onView: (assessmentId: string) => void
): DataTableMobileRow => {
  const completed = record.status === AssessmentStatus.Completed;
  return {
    title: displayUser(record),
    flagged: record.reviewFlagged,
    subtitle: record.interviewTemplate.name,
    statusLabel: completed ? 'Completed' : 'In Progress',
    statusTone: completed ? 'success' : 'warning',
    date: new Date(record.completedAt ?? record.createdAt).toLocaleDateString(),
    scoreValue: record.totalScore === null ? undefined : record.totalScore.toFixed(1),
    scoreLabel: record.label ?? 'Pending',
    scoreTone: record.label ? SCORE_LABEL_TONES[record.label] : 'muted',
    onOpen: completed ? () => onView(record.id) : undefined,
  };
};

export const htsSubmissionMobileRow = (
  record: AdminHtsSubmissionListItem,
  onView: (submissionId: string) => void
): DataTableMobileRow => {
  // The review gate alone, guest or not — same predicate as the desktop Score column.
  const pending = isHtsReviewPending(record);
  const scored = !pending && record.compositeScore !== null;
  return {
    title: displayUser(record),
    flagged: record.flaggedAnswerCount > 0,
    subtitle: record.interviewTemplateName,
    statusLabel: pending ? 'Needs review' : 'Submitted',
    statusTone: pending ? 'danger' : 'success',
    date: record.submittedAt ? new Date(record.submittedAt).toLocaleDateString() : undefined,
    scoreValue: scored ? String(record.compositeScore) : undefined,
    scoreLabel: scored && record.band !== null ? bandLabelFor(record.band) : 'Pending',
    scoreTone: scored && record.band !== null ? HTS_BAND_TONES[record.band] : 'muted',
    onOpen: () => onView(record.submissionId),
  };
};
